import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import CssBaseline from "@mui/material/CssBaseline";
import { Divider, ThemeProvider } from "@mui/material";
import {
  COLOR_DEFAULT_BACKGROUND,
  COLOR_ICON_BACKGROUND,
  COLOR_LIGHT_GREY,
  appTheme,
} from "./theme";
import Typography from "@mui/material/Typography";
import MobileMenu from "./MobileMenu";
import Clients from "./Clients";
import WorkExperienceBadge from "./WorkExperienceBadge";

const ListStyle = {
  display: "flex",
  fontSize: 16,
  pl: "2rem",
  textdecoration: "none",
  color: "#333",
  whiteSpace: "nowrap",
};

const serviceBadge = (iconComponent, title, subtitle) => {
  return (
    <Box
      p={3}
      maxWidth="100%"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        background: "#fff",
        border: "1px solid #e5e5e5",
        marginBottom: "1rem",
        borderRadius: 4,
      }}
    >
      <Box
        p={1}
        maxHeight={52}
        maxWidth={52}
        minHeight={52}
        minWidth={52}
        sx={{
          backgroundColor: COLOR_ICON_BACKGROUND, // Replace with your color variable
          borderRadius: "50%", // Ensures a perfect circle
          display: "flex", // Centers the icon
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {iconComponent}
      </Box>
      <Box py={1}>
        <Typography variant="h6" sx={{ fontFamily: "Sanchez" }}>
          {title.split("\n").map((line, index) => (
            <React.Fragment key={index}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </Typography>
        <Typography variant="body2" sx={{}}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

function App() {
  // Nav Bar
  const [scrolled, setScrolled] = useState(false);

  const experienceRef = useRef(null);
  const servicesRef = useRef(null);
  const contactRef = useRef(null);
  const topRef = useRef(null);
  const partenairesRef = useRef(null);

  const scrollToSection = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const boxRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      // Make sure scroll position is greater than 50px
      if (boxRef.current) {
        const scrollPosition = boxRef.current.scrollTop;
        setScrolled(scrollPosition > 50); // Update state based on scroll position
      }
    };

    const box = boxRef.current;
    if (box) {
      box.addEventListener("scroll", handleScroll);
    }

    // Cleanup the event listener on component unmount
    return () => {
      if (box) {
        box.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleEmailClick = () => {
    const email = "girardjon@hotmail.com";
    const subject = encodeURIComponent("Demande de réservation");
    const body = encodeURIComponent(
      `Bonjour, j'aimerais réserver un rendez-vous.\n\nNom : \nEntreprise : \nInformations : \n\nMerci beaucoup!`
    );

    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Box
        sx={{
          overflowY: "auto",
          height: "100vh", // Ensure the box fills the entire viewport
        }}
      >
        {/* Header */}
        <Box
          component="header"
          px={4}
          sx={{
            pt: scrolled ? 0 : 1, // Reduce padding on scroll
            pb: scrolled ? 0 : 1,
            pl: "5rem",
            display: "flex",
            top: 0,
            position: "sticky",
            backgroundColor: COLOR_DEFAULT_BACKGROUND,
            alignItems: "center",
            fontWeight: "500",
            zIndex: 100,
            transition: "all 0.3s ease-in-out", // Smooth animation
            "@media (max-width: 1100px)": {
              // Portrait or smaller screens
              pl: "2rem",
            },
          }}
        >
          <MobileMenu
            handleOnExperiencesClick={() => scrollToSection(experienceRef)}
            handleOnServicesClick={() => scrollToSection(servicesRef)}
            handleOnContactClick={() => scrollToSection(contactRef)}
            handleOnPartenairesClick={() => scrollToSection(partenairesRef)}
            handleOnTopClick={() => scrollToSection(topRef)}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              "@media (max-width: 1100px)": {
                // Portrait or smaller screens
                display: "none", // Change to column for portrait
              },
            }}
          >
            <Box
              sx={{
                display: { xs: "none" }, // Show only on mobile (xs) screens
              }}
            ></Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="h6"
                fontWeight={500}
                sx={{ cursor: "pointer" }}
                onClick={() => scrollToSection(topRef)}
              >
                Jonathan Girard
              </Typography>
              {/* <nav> */}
              <List component="ul" sx={ListStyle}>
                <ListItem
                  component="li"
                  onClick={() => scrollToSection(partenairesRef)}
                >
                  <ListItemButton sx={{ borderRadius: 50 }}>
                    Partenaires
                  </ListItemButton>
                </ListItem>
                <ListItem
                  component="li"
                  onClick={() => scrollToSection(experienceRef)}
                >
                  <ListItemButton sx={{ borderRadius: 50 }}>
                    Expériences
                  </ListItemButton>
                </ListItem>
                <ListItem
                  component="li"
                  onClick={() => scrollToSection(servicesRef)}
                >
                  <ListItemButton sx={{ borderRadius: 50 }}>
                    Services
                  </ListItemButton>
                </ListItem>
                {/* <ListItem component="li">
                  <ListItemButton sx={{ borderRadius: 50 }}>
                    About Me
                  </ListItemButton>
                </ListItem> */}
                <ListItem
                  component="li"
                  onClick={() => scrollToSection(contactRef)}
                >
                  <ListItemButton sx={{ borderRadius: 50 }}>
                    Contact
                  </ListItemButton>
                </ListItem>
              </List>
              {/* </nav> */}
            </Box>

            <Box sx={{ display: "flex", ml: 0 }}>
              <Button
                sx={{
                  borderRadius: 50,
                  py: 1,
                }}
                variant="contained"
                onClick={handleEmailClick}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    textTransform: "none",
                    fontWeight: "300",
                  }}
                >
                  Contactez-moi
                </Typography>
              </Button>
              {/* <Button
                  sx={{
                    borderRadius: 50,
                    fontWeight: "400",
                    py: 1.2
                  }}
                  variant="contained"
                >
                  See my work
                </Button> */}
            </Box>
            {/* <Button
              sx={{
                borderRadius: 50,
                mr: "4rem",
                fontWeight: "400",
                px: 3,
                py: 1.2,
              }}
              variant="outlined"
            >
              See my work
            </Button> */}
          </Box>
        </Box>

        <Box
          component="main"
          ref={topRef}
          sx={{
            display: "flex",
            justifyContent: "center", // Ensures equal distribution of sections
            alignItems: "start",
            flexDirection: "row",
            gap: 12, // Adds spacing between sections
            "@media (max-width: 1100px)": {
              flexDirection: "column", // Stack vertically on smaller screens
              alignItems: "center",
            },
          }}
        >
          {/* Left Section */}
          <Box
            component="section"
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "start",
              justifyContent: "end",
              pt: 11,
              "@media (max-width: 1100px)": {
                // Portrait or smaller screens
                pt: 2,
                flexDirection: "column", // Change to column for portrait
              },
            }}
          >
            <Box
              sx={{
                py: 0,
                ml: 12,
                "@media (max-width: 1100px)": {
                  // Portrait or smaller screens
                  my: 6,
                  ml: 4,
                },
              }}
            >
              <Box
                mb={4}
                sx={{
                  "@media (max-width: 1100px)": {
                    mb: 5,
                    width: "140px", // Smaller width for portrait or smaller screens
                  },
                  height: "140px",
                  width: "147px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "20px",
                }}
              >
                <img
                  src="/images/Jon_Girard.jpg"
                  alt="Jonathan Girard"
                  style={{
                    borderRadius: "20px",
                    boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                    width: "100%", // Ensures the image resizes according to the parent box
                  }}
                />
              </Box>

              <Box my={1}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: 32, fontFamily: "Sanchez" }}
                >
                  Je suis Jonathan Girard
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  maxWidth: "250px",
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                }}
              >
                Développeur basé à Québec, Canada
              </Typography>
            </Box>
          </Box>

          {/* Right Section */}
          <Box
            component="section"
            sx={{
              flex: 1, // Take equal space
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start", // Align content to the left
              marginLeft: "1rem", // Add spacing between sections
              pt: 6,
              "@media (max-width: 1100px)": {
                // Portrait or smaller screens
                alignItems: "center",
                mx: 2,
                pt: 0,
              },
            }}
          >
            <Box
              sx={{
                maxWidth: "600px",
                py: 4,
                "@media (max-width: 1100px)": {
                  py: 0,
                  px: 4,
                  pb: 4,
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Sanchez",
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                }}
                ref={partenairesRef}
              >
                Passionné de bâtir des solutions technologiques qui font une
                différence.
              </Typography>
            </Box>

            <Clients ref={experienceRef} />

            {/* Working Experience Section */}
            <Box component="section" mb={4}>
              <Divider
                sx={{
                  width: "100%", // Full width of its parent container
                  height: "1px", // Make it thicker if needed
                  backgroundColor: COLOR_LIGHT_GREY, // Set a visible color
                  my: 2,
                  opacity: 0.5,
                }}
              />

              <Box mb={3}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "Sanchez",
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                  }}
                >
                  Expérience de travail
                </Typography>
              </Box>
              <Box p={0}>
                <WorkExperienceBadge
                  logoUrl="/images/circonflex_logo.png"
                  title="Spécialiste en IA"
                  subtitle="Circonflex"
                  dateRange="Jun 2022 - Aug 2024"
                />
                <WorkExperienceBadge
                  logoUrl="/images/vooban_logo.png"
                  title="Team Lead"
                  subtitle="Vooban"
                  dateRange="Jun 2021 - Jun 2022"
                />
                <WorkExperienceBadge
                  logoUrl="/images/alfred_logo.jpg"
                  title="Développeur full stack"
                  subtitle="Alfred Technologies"
                  dateRange="Mar 2020 - Jun 2021"
                />
                <WorkExperienceBadge
                  logoUrl="/images/olympus_logo.jpg"
                  title={`Concepteur logiciel niveau 3 \nresponsable technique`}
                  subtitle="Olympus IMS"
                  dateRange="Oct 2019 - Mar 2020"
                />
                <WorkExperienceBadge
                  logoUrl="/images/vooban_logo.png"
                  title="Développeur frontend"
                  subtitle="Vooban"
                  dateRange="Nov 2018 - Oct 2019"
                />
                <WorkExperienceBadge
                  logoUrl="/images/gft_logo.png"
                  title="Développeur web"
                  subtitle="V-Neo Inc. now GFT"
                  dateRange="Apr 2018 - Nov 2018"
                />
                <WorkExperienceBadge
                  logoUrl="/images/zetec_logo.png"
                  title="Développeur logiciel"
                  subtitle="Zetec"
                  dateRange="Nov 2015 - Oct 2018"
                  ref={servicesRef}
                />
              </Box>
            </Box>

            {/* Services Section */}
            <Box component="section" sx={{ maxWidth: "600px" }}>
              <Divider
                sx={{
                  width: "100%", // Full width of its parent container
                  height: "1px", // Make it thicker if needed
                  backgroundColor: COLOR_LIGHT_GREY, // Set a visible color
                  my: 2,
                  opacity: 0.5,
                }}
              />
              <Box mb={3}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "Sanchez",
                    maxWidth: "400px",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                  }}
                >
                  Services & expertises
                </Typography>
              </Box>
              <Box>
                {serviceBadge(
                  <img
                    src="/icons/browser.png"
                    alt="Browser Icon"
                    width={24}
                  />,
                  "Web",
                  "Je crée des sites web dynamiques et performants, en utilisant des technologies modernes comme React et Next.js. Que ce soit pour un site vitrine, une application interactive ou une Progressive Web App (PWA), je développe des solutions adaptées à vos besoins."
                )}
                {serviceBadge(
                  <img
                    src="/icons/smartphone.png"
                    alt="Custom Icon"
                    width={24}
                  />,
                  "Mobile",
                  "Je conçois des applications mobiles performantes et adaptées, en utilisant des technologies proches des natives telles que React Native, pour garantir une expérience utilisateur fluide et accessible sur plusieurs plateformes mobiles."
                )}
                {serviceBadge(
                  <img
                    src="/icons/neural_net.png"
                    alt="Neural Net Icon"
                    width={24}
                  />,
                  "Intelligence Artificielle",
                  "Je vous aide à intégrer des modèles d’intelligence artificielle dans vos systèmes. De la recherche et de l’entraînement de modèles à leur déploiement et intégration dans votre infrastructure existante, je vous accompagne pour exploiter le plein potentiel des données et améliorer vos processus."
                )}
                {serviceBadge(
                  <img src="/icons/robot.png" alt="Custom Icon" width={24} />,
                  "Bots Intelligents",
                  "Je développe des bots intelligents capables d'automatiser des tâches complexes et d’interagir avec des utilisateurs de manière naturelle. Grâce à des modèles avancés, ces bots peuvent améliorer l’efficacité des processus métiers et enrichir l’expérience utilisateur."
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          component="footer"
          p={2}
          px={3}
          pr={6}
          m={2}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "black",
            borderRadius: 20,
            color: "white",
          }}
          ref={contactRef}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                py: "0.5",
                "@media (max-width: 1100px)": {
                  py: 0,
                },
              }}
            >
              <img
                src="/images/Jon_Girard.jpg"
                width="70px"
                style={{
                  borderRadius: 50,
                  border: "1.5px solid rgba(255, 255, 255, 0.19)",
                  boxShadow: "0px 2px 16px rgba(255, 255, 255, 0.20)",
                }}
                alt="Jon_Portrait"
              />

              <Box
                px={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  "@media (max-width: 1100px)": {
                    display: "none", // Hide on small screens (portrait)
                  },
                }}
              >
                <Box mb={0.5}>
                  <Typography variant="h6" sx={{ fontFamily: "Sanchez" }}>
                    Je suis Jonathan Girard
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: "250px",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                  }}
                >
                  Développeur basé à Québec, Canada
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Middle Section */}
          {/* <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)", // Two columns
              gap: 2, // Space between items
              columnGap: 4, // Add extra space between columns
              textAlign: "start",
              "@media (max-width: 600px)": {
                display: "none", // Hide on small screens (portrait)
              },
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ cursor: "pointer" }}
                onClick={() => scrollToSection(partenairesRef)}
              >
                Partenaires
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{ cursor: "pointer" }}
                onClick={() => scrollToSection(experienceRef)}
              >
                Expériences
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{ cursor: "pointer" }}
                onClick={() => scrollToSection(servicesRef)}
              >
                Services
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ cursor: "pointer" }}>
                Contact us
              </Typography>
            </Box>
          </Box> */}

          <Button
            sx={{
              borderRadius: 50,
              py: 1,
              color: "black",
              backgroundColor: "white",
            }}
            variant="contained"
            onClick={handleEmailClick}
          >
            <Typography
              variant="subtitle2"
              sx={{ textTransform: "none", fontWeight: "400" }}
            >
              Contactez-moi
            </Typography>
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
